import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Clock,
  Leaf,
  Mail,
  MapPin,
  Menu,
  Phone,
  Quote,
  Scissors,
  ShieldCheck,
  Shovel,
  Sparkles,
  Star,
  TreePine,
  UploadCloud,
  X,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";
import QuoteForm from "./components/QuoteForm";
import AdminDashboard from "./components/AdminDashboard";
import PublicWorkGallery from "./components/PublicWorkGallery";
import { trackEvent, trackEventOncePerSession } from "./lib/metrics";

const business = {
  name: "Precision Lawn Workx",
  legalName: "Precision Lawn Workx LLC",
  phone: "440-279-5530",
  email: "quotes@precisionlawnworkx.com",
  location: "Madison, Ohio & surrounding areas",
  tagline: "Your yard, done right — with precision.",
};

const services = [
  {
    id: "mowing",
    label: "Lawn Mowing & Maintenance",
    short: "Clean cuts, trimming, and routine upkeep",
    icon: Leaf,
    accent: "from-lime-400 to-green-700",
    eyebrow: "Reliable lawn care",
    title: "Sharp mowing lines. Clean edges. No sloppy work.",
    copy:
      "Precision Lawn Workx keeps yards looking clean with dependable mowing, trimming, maintenance, and detail-focused lawn care.",
    bullets: [
      "Weekly or one-time mowing",
      "Clean trimming around edges",
      "Basic lawn maintenance",
      "Fresh mowing lines",
      "Residential yard care",
      "Reliable scheduling",
    ],
  },
  {
    id: "landscaping",
    label: "Landscaping",
    short: "Curb appeal, cleanup, and outdoor refreshes",
    icon: Shovel,
    accent: "from-green-500 to-emerald-800",
    eyebrow: "Outdoor upgrades",
    title: "Landscaping that makes the whole property look handled.",
    copy:
      "From simple refreshes to bed cleanup and landscape improvements, the goal is a clean, sharp yard that looks intentional.",
    bullets: [
      "Landscape refreshes",
      "Bed cleanup",
      "Curb appeal improvements",
      "Property cleanup",
      "Planting area prep",
      "Outdoor detail work",
    ],
  },
  {
    id: "mulching",
    label: "Mulching & Bed Edging",
    short: "Sharper beds and cleaner borders",
    icon: Scissors,
    accent: "from-lime-500 to-green-900",
    eyebrow: "Clean bed work",
    title: "Mulch beds that actually look finished.",
    copy:
      "Fresh mulch and clean edging can change the whole look of a property fast. This is one of the strongest visual services for the site.",
    bullets: [
      "Mulch installation",
      "Bed edging",
      "Garden bed cleanup",
      "Weed cleanup before mulch",
      "Fresh border lines",
      "Spring property refreshes",
    ],
  },
  {
    id: "trimming",
    label: "Bush & Shrub Trimming",
    short: "Clean shape, less overgrowth",
    icon: TreePine,
    accent: "from-emerald-400 to-green-800",
    eyebrow: "Trimmed and controlled",
    title: "Overgrown bushes make a property look neglected.",
    copy:
      "Bush and shrub trimming keeps the yard looking intentional, clean, and easier to maintain through the season.",
    bullets: [
      "Bush trimming",
      "Shrub shaping",
      "Overgrowth cleanup",
      "Walkway clearing",
      "Front-bed cleanup",
      "Seasonal trimming",
    ],
  },
  {
    id: "cleanups",
    label: "Spring & Fall Cleanups",
    short: "Leaves, debris, and seasonal mess",
    icon: UploadCloud,
    accent: "from-orange-400 to-green-800",
    eyebrow: "Seasonal cleanup",
    title: "The yard reset before or after the season hits.",
    copy:
      "Spring and fall cleanups help customers get their property back under control without burning a whole weekend doing it themselves.",
    bullets: [
      "Leaf cleanup",
      "Yard debris cleanup",
      "Spring cleanup",
      "Fall cleanup",
      "Bed cleanup",
      "General outdoor reset",
    ],
  },
  {
    id: "tree-work",
    label: "Tree Work",
    short: "Limbs, brush, and outdoor debris",
    icon: TreePine,
    accent: "from-green-600 to-slate-950",
    eyebrow: "Brush and limb work",
    title: "Tree debris and brush piles handled cleanly.",
    copy:
      "For limbs, brush, smaller tree work, and outdoor messes, Precision Lawn Workx helps clear the property and haul away the debris.",
    bullets: [
      "Limb cleanup",
      "Brush removal",
      "Small tree work",
      "Storm debris cleanup",
      "Outdoor debris hauling",
      "Property clearing",
    ],
  },
  {
    id: "junk-removal",
    label: "Junk Removal",
    short: "Yard junk, debris, and unwanted items",
    icon: Trash2,
    accent: "from-orange-500 to-slate-950",
    eyebrow: "Removal services",
    title: "Junk removal belongs here, but don’t let it hijack the brand.",
    copy:
      "Junk removal works best as an add-on service for yard cleanups, debris removal, and property cleanup jobs.",
    bullets: [
      "Junk removal",
      "Yard debris removal",
      "Small cleanouts",
      "Brush piles",
      "Outdoor unwanted items",
      "Cleanup hauling",
    ],
  },
];

const trustPoints = [
  {
    icon: BadgeCheck,
    label: "Reliable",
    copy: "People hire lawn services because they want the job handled without chasing someone down.",
  },
  {
    icon: ShieldCheck,
    label: "Quality work every time",
    copy: "Clean results, sharp lines, and consistent presentation are the whole point.",
  },
  {
    icon: Quote,
    label: "Free estimates",
    copy: "The call-to-action is simple: call, text, or submit a request for a free estimate.",
  },
  {
    icon: Star,
    label: "Affordable",
    copy: "Affordable does not mean cheap-looking. The site still needs to feel professional.",
  },
];

const fallingGrass = Array.from({ length: 18 }, (_, i) => ({
  left: `${(i * 5.8 + (i % 4) * 6) % 100}%`,
  delay: `${-(i * 1.15)}s`,
  duration: `${16 + (i % 5) * 1.7}s`,
  size: 13 + (i % 4) * 4,
  opacity: 0.2 + (i % 3) * 0.04,
}));

const introGrassBlades = Array.from({ length: 96 }, (_, i) => ({
  left: `${(i / 95) * 100}%`,
  height: 64 + (i % 7) * 11 + ((i * 3) % 10),
  rotate: -18 + (i % 10) * 4,
  delay: `${(i % 14) * 0.024}s`,
  width: 7 + (i % 3),
}));

const footerBladeData = Array.from({ length: 150 }, (_, i) => ({
  left: `${(i / 149) * 100}%`,
  height: 58 + (i % 9) * 10 + ((i * 5) % 12),
  width: 6 + (i % 3),
  rotate: -20 + (i % 11) * 4,
  delay: `${(i % 18) * 0.08}s`,
}));

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

function scrollToId(id) {
  const element = document.getElementById(id);
  if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
}

function trackCall(location) {
  trackEvent("call_click", {
    phone: business.phone,
    location,
  });
}

function SiteBackground({ bottomGrass = true, className = "" }) {
  return (
    <div className={`absolute inset-0 -z-10 overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-[#070a07]" />
      <div className="absolute inset-0 lawn-stripes opacity-70" />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(132,204,22,0.22),transparent_30%),radial-gradient(circle_at_85%_15%,rgba(249,115,22,0.14),transparent_26%),linear-gradient(135deg,rgba(4,7,5,0.94),rgba(13,32,17,0.9)_45%,rgba(4,8,5,0.96))]" />

      {bottomGrass && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-44">
          <div className="corner-grass corner-grass-left" />
          <div className="corner-grass corner-grass-right" />
        </div>
      )}

      <div className="pointer-events-none absolute inset-0">
        {fallingGrass.map((blade, index) => (
          <span
            key={index}
            className="grass-fall"
            style={{
              left: blade.left,
              width: `${blade.size}px`,
              height: `${blade.size * 2.1}px`,
              animationDelay: blade.delay,
              animationDuration: blade.duration,
              opacity: blade.opacity,
            }}
          />
        ))}
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.72),rgba(0,0,0,0.18)_52%,rgba(0,0,0,0.55))]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/70" />
    </div>
  );
}

function IntroGrass() {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHidden(true), 2450);
    return () => clearTimeout(timer);
  }, []);

  if (hidden) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[999] overflow-hidden intro-overlay">
      <div className="intro-dark" />

      <div className="intro-grass-stage">
        <div className="intro-grass-bed">
          {introGrassBlades.map((blade, index) => (
            <span
              key={index}
              className="intro-blade"
              style={{
                left: blade.left,
                height: `${blade.height}px`,
                width: `${blade.width}px`,
                transform: `rotate(${blade.rotate}deg)`,
                animationDelay: blade.delay,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function GrassBladeCluster({ side = "left", count = 42 }) {
  const blades = Array.from({ length: count }, (_, i) => ({
    left: `${(i / (count - 1)) * 100}%`,
    height: 46 + (i % 7) * 9 + ((i * 3) % 8),
    width: 6 + (i % 3),
    rotate: -22 + (i % 9) * 4,
    delay: `${i * 0.012}s`,
  }));

  return (
    <div className={`blade-cluster blade-cluster-${side}`}>
      <div className="blade-cluster-inner">
        {blades.map((blade, index) => (
          <span
            key={`${side}-${index}`}
            className="persistent-blade"
            style={{
              left: blade.left,
              height: `${blade.height}px`,
              width: `${blade.width}px`,
              transform: `rotate(${blade.rotate}deg)`,
              animationDelay: blade.delay,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function PersistentCornerGrass() {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 h-28 overflow-hidden sm:h-32">
      <GrassBladeCluster side="left" count={44} />
      <GrassBladeCluster side="right" count={44} />
    </div>
  );
}

function LogoMark({ compact = false }) {
  return (
    <div className={`flex items-center gap-3 ${compact ? "" : "min-w-0"}`}>
      <div className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-2xl bg-black shadow-lg shadow-black/20 ring-1 ring-lime-400/30">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_35%_20%,rgba(163,230,53,0.9),transparent_35%),linear-gradient(135deg,#111,#064e3b)]" />
        <Leaf className="relative h-6 w-6 text-white" />
      </div>

      <div className="leading-none">
        <p className="text-base font-black uppercase tracking-tight">Precision</p>
        <p className="mt-1 text-xs font-black uppercase tracking-[0.2em] text-lime-600">
          Lawn Workx
        </p>
      </div>
    </div>
  );
}

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(services[0].id);

  const selected = useMemo(
    () => services.find((service) => service.id === selectedService) || services[0],
    [selectedService]
  );

  const SelectedIcon = selected.icon;

  useEffect(() => {
    if (window.location.pathname !== "/admin") {
      trackEventOncePerSession("page_view", {
        path: window.location.pathname,
        title: document.title,
      });
    }
  }, []);

  if (window.location.pathname === "/admin") {
    return <AdminDashboard />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f6f8f2] text-slate-950 selection:bg-lime-300 selection:text-black">
      <IntroGrass />
      <PersistentCornerGrass />

      <header className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-white/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <button onClick={() => scrollToId("home")} className="text-left">
            <LogoMark />
          </button>

          <nav className="hidden items-center gap-7 text-sm font-black text-slate-700 lg:flex">
            <button onClick={() => scrollToId("services")} className="hover:text-green-700">
              Services
            </button>
            <button onClick={() => scrollToId("work")} className="hover:text-green-700">
              Work
            </button>
            <button onClick={() => scrollToId("process")} className="hover:text-green-700">
              How It Works
            </button>
            <button onClick={() => scrollToId("about")} className="hover:text-green-700">
              About
            </button>
            <a
              href={`tel:${business.phone}`}
              onClick={() => trackCall("header")}
              className="rounded-full bg-black px-5 py-3 text-white shadow-lg shadow-black/15 transition hover:-translate-y-0.5 hover:bg-green-800"
            >
              Call {business.phone}
            </a>
          </nav>

          <button
            className="grid h-11 w-11 place-items-center rounded-xl border border-black/10 lg:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="border-t border-black/10 bg-white px-5 py-4 lg:hidden">
            <div className="mx-auto grid max-w-7xl gap-2 text-sm font-black">
              {["services", "work", "process", "about", "quote"].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    scrollToId(item);
                    setMobileOpen(false);
                  }}
                  className="rounded-xl px-4 py-3 text-left capitalize hover:bg-lime-50"
                >
                  {item === "quote" ? "Free Estimate" : item}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <main id="home" className="pt-20">
        <section className="relative isolate overflow-hidden bg-black text-white">
          <SiteBackground />

          <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-16 lg:grid-cols-[1fr_0.92fr] lg:px-8 lg:py-24 xl:py-28">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.65, delay: 1.1 }}
            >
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-lime-300/20 bg-white/10 px-4 py-2 text-sm font-black text-lime-100 shadow-xl shadow-black/20 backdrop-blur">
                <Sparkles className="h-4 w-4 text-lime-300" />
                Proudly serving {business.location}
              </div>

              <h1 className="max-w-4xl text-5xl font-black uppercase leading-[0.88] tracking-tight sm:text-6xl lg:text-7xl">
                Your yard, done right.
              </h1>

              <p className="mt-6 max-w-2xl text-xl font-black uppercase tracking-tight text-lime-300 sm:text-2xl">
                With Precision.
              </p>

              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                Lawn mowing, landscaping, mulching, shrub trimming, seasonal cleanups,
                tree work, and junk removal for Madison, Ohio and surrounding areas.
              </p>

              <div className="mt-8 grid gap-3 sm:flex sm:flex-wrap">
                <a
                  href={`tel:${business.phone}`}
                  onClick={() => trackCall("hero_cta")}
                  className="group inline-flex items-center justify-center gap-3 rounded-2xl bg-lime-400 px-6 py-4 text-base font-black text-black shadow-2xl shadow-black/30 transition hover:-translate-y-0.5 hover:bg-lime-300"
                >
                  Call or Text for a Free Estimate
                  <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
                </a>

                <button
                  onClick={() => scrollToId("services")}
                  className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-6 py-4 text-base font-black text-white backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/15"
                >
                  View Services
                </button>
              </div>

              <div className="mt-8 flex flex-wrap gap-3 text-sm font-bold text-slate-300">
                <span className="rounded-full border border-white/10 bg-white/10 px-4 py-2">Reliable</span>
                <span className="rounded-full border border-white/10 bg-white/10 px-4 py-2">Affordable</span>
                <span className="rounded-full border border-white/10 bg-white/10 px-4 py-2">Quality Work Every Time</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 1.25 }}
              className="relative"
            >
              <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-lime-400/25 via-white/5 to-orange-500/20 blur-3xl" />

              <div className="relative overflow-hidden rounded-[2.2rem] border border-white/10 bg-white/[0.08] p-4 shadow-2xl shadow-black/30 backdrop-blur-md">
                <div className="rounded-[1.65rem] bg-black/80 p-5 shadow-inner shadow-black/30 ring-1 ring-white/10">
                  <img
                    src="/precision-logo.jpg"
                    alt="Precision Lawn Workx logo"
                    className="mx-auto max-h-[315px] w-full object-contain"
                  />
                </div>

                <div className="mt-4 rounded-[1.5rem] border border-lime-300/20 bg-black/45 p-5">
                  <p className="text-sm font-black uppercase tracking-[0.25em] text-lime-300">
                    Free estimates
                  </p>
                  <a
                    href={`tel:${business.phone}`}
                    onClick={() => trackCall("hero_logo_card")}
                    className="mt-2 block text-4xl font-black tracking-tight text-white sm:text-5xl"
                  >
                    {business.phone}
                  </a>
                  <p className="mt-2 text-sm font-bold uppercase tracking-[0.16em] text-slate-300">
                    Lawn mowing & landscaping
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="border-b border-black/10 bg-white py-5">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-5 text-sm font-black text-slate-600 lg:px-8">
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-green-700" />
              {business.location}
            </span>

            <a
              href={`tel:${business.phone}`}
              onClick={() => trackCall("info_strip")}
              className="flex items-center gap-2 hover:text-green-700"
            >
              <Phone className="h-4 w-4 text-green-700" />
              {business.phone}
            </a>

            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-700" />
              Call or text today
            </span>

            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-700" />
              Free estimates
            </span>
          </div>
        </section>

        <section id="services" className="scroll-mt-24 px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.3em] text-green-700">
                  Services
                </p>

                <h2 className="mt-3 max-w-3xl text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">
                  Lawn care, cleanup, and outdoor work without the runaround.
                </h2>
              </div>

              <p className="max-w-2xl text-lg leading-8 text-slate-600 lg:justify-self-end">
                The site should sell the result fast: clean mowing lines, sharp beds,
                trimmed shrubs, seasonal cleanup, and an easy way to request a free estimate.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {services.map((service) => {
                const Icon = service.icon;
                const active = selectedService === service.id;

                return (
                  <button
                    key={service.id}
                    onClick={() => {
                      setSelectedService(service.id);
                      setTimeout(() => scrollToId("service-preview"), 0);
                    }}
                    className={`group relative flex min-h-[255px] flex-col overflow-hidden rounded-[1.75rem] border px-5 pb-5 pt-5 text-left shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl sm:min-h-[290px] sm:pb-6 ${
                      active
                        ? "border-lime-300 bg-lime-50 shadow-green-900/10"
                        : "border-black/10 bg-white hover:border-lime-300 hover:bg-lime-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className={`grid h-13 w-13 place-items-center rounded-2xl bg-gradient-to-br ${service.accent} p-3 shadow-lg`}>
                        <Icon className="h-7 w-7 text-white" />
                      </div>

                      <ArrowRight className="mt-2 h-5 w-5 text-slate-300 transition group-hover:translate-x-1 group-hover:text-green-700" />
                    </div>

                    <h3 className="mt-6 text-[1.7rem] font-black leading-[1.04] tracking-tight sm:text-[2rem]">
                      {service.label}
                    </h3>

                    <p className="mt-3 text-sm font-semibold leading-6 text-slate-600">
                      {service.short}
                    </p>

                    <div className="mt-auto pt-5">
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.18em] transition ${
                          active
                            ? "bg-green-700 text-white shadow-lg"
                            : "bg-slate-100 text-slate-600 group-hover:bg-green-700 group-hover:text-white group-hover:shadow-lg"
                        }`}
                      >
                        {active ? "Selected Service" : "Preview Service"}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section id="service-preview" className="relative isolate scroll-mt-28 px-5 pb-10 lg:px-8">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] border border-black/10 bg-slate-950 text-white shadow-2xl shadow-slate-900/15">
            <SiteBackground bottomGrass={false} className="opacity-90" />

            <div className="grid gap-0 lg:grid-cols-[0.85fr_1.15fr]">
              <div className={`relative isolate bg-gradient-to-br ${selected.accent} p-8 sm:p-10`}>
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white/10 via-transparent to-black/20" />
                <SelectedIcon className="h-14 w-14" />

                <p className="mt-8 text-sm font-black uppercase tracking-[0.28em] text-white/75">
                  {selected.eyebrow}
                </p>

                <h3 className="mt-3 text-3xl font-black leading-tight tracking-tight sm:text-4xl">
                  {selected.title}
                </h3>

                <p className="mt-5 text-lg leading-8 text-white/85">{selected.copy}</p>

                <button
                  onClick={() => scrollToId("quote")}
                  className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-white px-6 py-4 font-black text-slate-950 transition hover:-translate-y-0.5"
                >
                  Quote this service <ArrowRight className="h-5 w-5" />
                </button>
              </div>

              <div className="relative grid content-center gap-4 p-6 sm:p-8 lg:p-10">
                <p className="text-sm font-black uppercase tracking-[0.3em] text-lime-300">
                  Common requests
                </p>

                <div className="grid gap-3 sm:grid-cols-2">
                  {selected.bullets.map((bullet) => (
                    <div key={bullet} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-4 font-bold text-slate-200 backdrop-blur">
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-lime-300" />
                      {bullet}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {services.map((service) => {
          const Icon = service.icon;

          return (
            <section key={service.id} id={service.id} className="scroll-mt-28 px-5 py-10 lg:px-8">
              <div className="mx-auto max-w-7xl rounded-[2.25rem] border border-black/10 bg-white p-6 shadow-sm md:p-8">
                <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                  <div>
                    <div className={`mb-5 inline-grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br ${service.accent} shadow-lg`}>
                      <Icon className="h-7 w-7 text-white" />
                    </div>

                    <p className="text-sm font-black uppercase tracking-[0.3em] text-green-700">
                      {service.eyebrow}
                    </p>

                    <h3 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
                      {service.title}
                    </h3>

                    <p className="mt-5 text-lg leading-8 text-slate-600">{service.copy}</p>

                    <button
                      onClick={() => scrollToId("quote")}
                      className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-black px-5 py-3 font-black text-white transition hover:-translate-y-0.5 hover:bg-green-800"
                    >
                      Quote {service.label} <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {service.bullets.map((bullet) => (
                      <div key={bullet} className="rounded-2xl bg-slate-50 p-4 font-bold text-slate-700 ring-1 ring-black/5">
                        <CheckCircle2 className="mb-3 h-5 w-5 text-green-600" />
                        {bullet}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        <section id="process" className="scroll-mt-24 bg-white px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-sm font-black uppercase tracking-[0.3em] text-green-700">
                How it works
              </p>

              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">
                Simple quote process. No bloated nonsense.
              </h2>

              <p className="mt-5 text-lg leading-8 text-slate-600">
                Customers can call, text, or submit their job details through the quote form.
              </p>
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                {
                  step: "01",
                  title: "Choose the service",
                  copy: "Mowing, landscaping, cleanup, trimming, tree work, or junk removal.",
                  icon: Leaf,
                },
                {
                  step: "02",
                  title: "Request a free quote",
                  copy: "Customers submit job details, contact info, location, and photos if they have them.",
                  icon: Quote,
                },
                {
                  step: "03",
                  title: "Get the yard handled",
                  copy: "Precision Lawn Workx LLC reviews the request and follows up with a free estimate.",
                  icon: CheckCircle2,
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <div key={item.step} className="rounded-[2rem] border border-black/10 bg-slate-50 p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-black text-green-700">{item.step}</span>
                      <Icon className="h-6 w-6 text-slate-400" />
                    </div>

                    <h3 className="mt-8 text-2xl font-black">{item.title}</h3>
                    <p className="mt-3 leading-7 text-slate-600">{item.copy}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <PublicWorkGallery SiteBackground={SiteBackground} />

        <section id="about" className="scroll-mt-24 px-5 py-20 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="relative isolate overflow-hidden rounded-[2.25rem] bg-black p-8 text-white shadow-2xl shadow-black/20 sm:p-10">
              <SiteBackground bottomGrass={false} className="opacity-80" />

              <div className="relative">
                <p className="text-sm font-black uppercase tracking-[0.3em] text-lime-300">
                  {business.name}
                </p>

                <h2 className="mt-4 text-4xl font-black tracking-tight">
                  Your yard, done right — with precision.
                </h2>

                <p className="mt-5 text-lg leading-8 text-slate-300">
                  Precision Lawn Workx provides lawn mowing, landscaping, trimming,
                  cleanups, tree work, and junk removal for Madison, Ohio and surrounding areas.
                </p>

                <a
                  href={`tel:${business.phone}`}
                  onClick={() => trackCall("about_section")}
                  className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-lime-400 px-6 py-4 font-black text-black transition hover:-translate-y-0.5 hover:bg-lime-300"
                >
                  Call {business.phone} <ArrowRight className="h-5 w-5" />
                </a>
              </div>
            </div>

            <div className="rounded-[2.25rem] border border-black/10 bg-white p-8 shadow-sm sm:p-10">
              <p className="text-sm font-black uppercase tracking-[0.3em] text-slate-400">
                Why choose Precision
              </p>

              <h3 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
                Clean work. Clear service. Sharp presentation.
              </h3>

              <p className="mt-5 text-lg leading-8 text-slate-600">
                The site needs to make the company feel dependable immediately. The visuals
                should look like lawn care, not a random template painted green.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {trustPoints.map((point) => {
                  const Icon = point.icon;

                  return (
                    <div key={point.label} className="rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
                      <Icon className="h-6 w-6 text-green-700" />
                      <h4 className="mt-4 font-black">{point.label}</h4>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{point.copy}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section id="quote" className="scroll-mt-24 px-5 pb-20 lg:px-8">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-slate-950 text-white shadow-2xl shadow-slate-900/20">
            <SiteBackground bottomGrass={false} className="opacity-90" />

            <div className="relative grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="relative isolate p-8 sm:p-10 lg:p-12">
                <p className="text-sm font-black uppercase tracking-[0.3em] text-lime-300">
                  Free quote request
                </p>

                <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">
                  Tell Precision what needs done.
                </h2>

                <p className="mt-5 text-lg leading-8 text-slate-300">
                  Call, text, or submit the form with your job details. Precision Lawn Workx LLC will review the request and follow up with a free estimate.
                </p>

                <div className="mt-8 grid gap-4 text-sm font-bold text-slate-300">
                  <a
                    href={`tel:${business.phone}`}
                    onClick={() => trackCall("quote_section")}
                    className="flex items-center gap-3 hover:text-lime-300"
                  >
                    <Phone className="h-5 w-5 text-lime-300" />
                    {business.phone}
                  </a>

                  <a
                    href={`mailto:${business.email}`}
                    onClick={() =>
                      trackEvent("email_click", {
                        email: business.email,
                        location: "quote_section",
                      })
                    }
                    className="flex items-center gap-3 hover:text-lime-300"
                  >
                    <Mail className="h-5 w-5 text-lime-300" />
                    {business.email}
                  </a>

                  <p className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-lime-300" />
                    {business.location}
                  </p>
                </div>
              </div>

              <QuoteForm services={services} business={business} />
            </div>
          </div>
        </section>
      </main>

      <footer className="relative isolate overflow-hidden bg-[#050805] px-5 pb-44 pt-16 text-white sm:pb-32 lg:px-8">
        <SiteBackground bottomGrass={false} className="opacity-65" />

        <div className="relative z-30 mx-auto grid max-w-7xl gap-10 md:grid-cols-[1fr_auto] md:items-start">
          <div>
            <LogoMark compact />

            <p className="mt-4 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              Lawn Mowing • Landscaping • Mulching • Trimming • Cleanups • Tree Work • Junk Removal
            </p>

            <p className="mt-5 text-lg font-black text-lime-300">
              Call or text for a free estimate: {business.phone}
            </p>

            <p className="mt-4 text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
              © {new Date().getFullYear()} {business.legalName}. All rights reserved.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 text-sm font-black text-slate-300">
            <button onClick={() => scrollToId("services")} className="hover:text-lime-300">Services</button>
            <button onClick={() => scrollToId("work")} className="hover:text-lime-300">Work</button>
            <button onClick={() => scrollToId("quote")} className="hover:text-lime-300">Free Estimate</button>
          </div>
        </div>

        <div className="footer-blade-bed">
          {footerBladeData.map((blade, index) => (
            <span
              key={index}
              className="footer-blade"
              style={{
                left: blade.left,
                height: `${blade.height}px`,
                width: `${blade.width}px`,
                transform: `rotate(${blade.rotate}deg)`,
                animationDelay: blade.delay,
              }}
            />
          ))}
        </div>
      </footer>
    </div>
  );
}

export default App;